import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

export interface Message {
  id: string;
  contact_id: string | null;
  lead_id: string | null;
  owner_session_id: string | null;
  body: string;
  direction: 'inbound' | 'outbound';
  channel: string;
  provider: string | null;
  status: string;
  from_e164: string | null;
  to_e164: string | null;
  sent_at: string | null;
  received_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  created_at: string;
  source: string | null;
  source_reference_id: string | null;
  // Media fields
  media_type: string | null;
  media_url: string | null;
  media_storage_path: string | null;
  media_filename: string | null;
  media_size_bytes: number | null;
  media_mimetype: string | null;
  sent_by_app_user_id: string | null;
}

interface UseConversationMessagesOptions {
  contactId?: string | null;
  leadId?: string | null;
  sessionId?: string | null;
  phoneNumber?: string | null;
  inboxChannel?: string | null;
  limit?: number;
}

interface MediaOptions {
  mediaType?: 'image' | 'document' | 'audio' | 'video';
  mediaUrl?: string;
  mediaBase64?: string;
  filename?: string;
  caption?: string;
  mimetype?: string;
  storagePath?: string;
  fileSize?: number;
}

interface UseConversationMessagesReturn {
  messages: Message[];
  isLoading: boolean;
  error: Error | null;
  isConnected: boolean;
  sendMessage: (body: string, mediaOptions?: MediaOptions) => Promise<{ success: boolean; error?: string }>;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  refetch: () => Promise<void>;
}

export function useConversationMessages({
  contactId,
  leadId,
  sessionId,
  phoneNumber,
  inboxChannel,
  limit = 50,
}: UseConversationMessagesOptions): UseConversationMessagesReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  
  // Ref para evitar duplicatas durante optimistic updates
  const pendingMessageIds = useRef<Set<string>>(new Set());
  const isSendingRef = useRef(false);
  const pendingMessageBody = useRef<string | null>(null);
  
  // Helper para normalizar body para comparação
  const normalizeBody = useCallback((text: string | null | undefined) => 
    text?.trim().toLowerCase().replace(/\s+/g, ' ') || '', []);

  // Carregar mensagens iniciais
  const loadMessages = useCallback(async () => {
    if (!contactId && !leadId && !phoneNumber && !sessionId) {
      setMessages([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Unified support inbox: include all CS-relevant sources (Twilio + Central + system notices)
      const allowedSources = [
        'twilio_divus',
        'wppconnect_shared',
        'evolution_shared',
        'whatsapp_app',
        'optimistic',
        'system_notice',
        'internal_note',
      ];


      let query = supabase
        .from('messages')
        .select('*')
        .eq('channel', 'whatsapp')
        .in('source', allowedSources)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (sessionId && contactId) {
        // Atendimento panel: only show messages tied to a support session
        // (or system notices / internal notes). Commercial-thread messages
        // have owner_session_id=NULL and must NOT appear here.
        query = query
          .eq('contact_id', contactId)
          .or('owner_session_id.not.is.null,source.in.(system_notice,internal_note,twilio_divus)');
      } else if (sessionId) {
        query = query.eq('owner_session_id', sessionId);
      } else if (contactId) {
        query = query.eq('contact_id', contactId);
      } else if (leadId) {
        query = query.eq('lead_id', leadId);
      } else if (phoneNumber) {
        const cleanPhone = phoneNumber.replace(/\D/g, '');
        query = query.or(`from_e164.ilike.%${cleanPhone}%,to_e164.ilike.%${cleanPhone}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      console.log('[useConversationMessages] Loaded', data?.length, 'messages for', { contactId, leadId, sessionId });
      // Reverse to show oldest first (we fetched newest first with DESC)
      setMessages(((data || []) as Message[]).reverse());
      setHasMore((data?.length || 0) >= limit);
    } catch (err) {
      console.error('[useConversationMessages] Load error:', err);
      setError(err instanceof Error ? err : new Error('Failed to load messages'));
    } finally {
      setIsLoading(false);
    }
  }, [contactId, leadId, sessionId, phoneNumber, limit]);

  // Carregar mais mensagens (scroll infinito)
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading || messages.length === 0) return;

    const oldestMessage = messages[0];
    
    try {
      const allowedSourcesMore = [
        'twilio_divus',
        'wppconnect_shared',
        'evolution_shared',
        'whatsapp_app',
        'optimistic',
        'system_notice',
        'internal_note',
      ];


      let query = supabase
        .from('messages')
        .select('*')
        .eq('channel', 'whatsapp')
        .in('source', allowedSourcesMore)
        .lt('created_at', oldestMessage.created_at)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (sessionId && contactId) {
        query = query
          .eq('contact_id', contactId)
          .or('owner_session_id.not.is.null,source.in.(system_notice,internal_note)');
      } else if (contactId) {
        query = query.eq('contact_id', contactId);
      } else if (sessionId) {
        query = query.eq('owner_session_id', sessionId);
      } else if (leadId) {
        query = query.eq('lead_id', leadId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        // Adicionar no início (mensagens mais antigas)
        setMessages(prev => [...(data as Message[]).reverse(), ...prev]);
        setHasMore(data.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('[useConversationMessages] Load more error:', err);
    }
  }, [contactId, leadId, sessionId, hasMore, isLoading, messages, limit]);

  // Enviar mensagem com optimistic update
  const sendMessage = useCallback(async (
    body: string,
    mediaOptions?: MediaOptions
  ): Promise<{ success: boolean; error?: string }> => {
    if (!phoneNumber && !contactId && !leadId) {
      return { success: false, error: 'No recipient specified' };
    }

    isSendingRef.current = true;

    // Criar ID temporário para optimistic update
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    pendingMessageIds.current.add(tempId);
    
    // Salvar body para matching posterior
    pendingMessageBody.current = body || mediaOptions?.caption || `[${mediaOptions?.mediaType || 'mensagem'}]`;

    // Optimistic update - adicionar mensagem imediatamente
    const optimisticMessage: Message = {
      id: tempId,
      contact_id: contactId || null,
      lead_id: leadId || null,
      owner_session_id: sessionId || null,
      body: body || mediaOptions?.caption || `[${mediaOptions?.mediaType || 'mensagem'}]`,
      direction: 'outbound',
      channel: 'whatsapp',
      provider: 'wppconnect',
      status: 'sending',
      from_e164: null,
      to_e164: phoneNumber || null,
      sent_at: new Date().toISOString(),
      received_at: null,
      delivered_at: null,
      read_at: null,
      created_at: new Date().toISOString(),
      media_type: mediaOptions?.mediaType || null,
      media_url: mediaOptions?.mediaUrl || null,
      media_storage_path: null,
      media_filename: mediaOptions?.filename || null,
      media_mimetype: mediaOptions?.mimetype || null,
      media_size_bytes: null,
      sent_by_app_user_id: null,
      source: 'optimistic',
      source_reference_id: null,
    };

    // Adicionar mensagem otimista à lista
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      // Channel-transition notice is intentionally NOT inserted here. It is inserted
      // exclusively by the template-send flow (SendTemplateModal → onTemplateSent in
      // CustomerService.tsx), because only a template officially opens the conversation
      // on the new Atendimento number. Inserting it on any outbound would cause the
      // notice to appear without a template above it (e.g. inside an active 24h window).


      // All /customer-service outbound now routes through Twilio support number.
      const response = await supabase.functions.invoke('twilio-whatsapp', {
        body: {
          action: 'sendMessage',
          to: phoneNumber,
          body: body,
          contactId,
          leadId,
          sessionId,
          channel: 'support',
          ...(mediaOptions?.mediaUrl && { mediaUrl: mediaOptions.mediaUrl }),
          ...(mediaOptions?.filename && { filename: mediaOptions.filename }),
          ...(mediaOptions?.mimetype && { mimetype: mediaOptions.mimetype }),
          ...(mediaOptions?.storagePath && { storagePath: mediaOptions.storagePath }),
          ...(mediaOptions?.fileSize && { fileSize: mediaOptions.fileSize }),
        },
      });

      // When the edge function returns a non-2xx status, supabase-js sets
      // response.error (FunctionsHttpError) and may NOT parse the body into
      // response.data. Read the real PT-BR message from the Response in
      // error.context when available.
      let result = response.data as { success?: boolean; error?: string; message?: { id?: string } } | null;

      if (response.error) {
        let serverError: string | undefined = result?.error;
        try {
          const ctx: any = (response.error as any)?.context;
          if (ctx && typeof ctx.json === 'function') {
            const parsed = await ctx.clone().json();
            serverError = parsed?.error || serverError;
          } else if (ctx && typeof ctx.text === 'function') {
            const txt = await ctx.clone().text();
            try { serverError = JSON.parse(txt)?.error || serverError; } catch { /* ignore */ }
          }
        } catch (e) {
          console.warn('[useConversationMessages] Could not parse error body:', e);
        }
        throw new Error(serverError || response.error.message || 'Falha ao enviar mensagem');
      }

      if (!result?.success) {
        throw new Error(result?.error || 'Falha ao enviar mensagem');
      }

      // Persist attachment metadata so MessageBubble can render the media.
      // The edge function inserts messages.media_* itself now, but we also
      // keep message_attachments as an auditable record (matches ChatCore flow).
      const insertedMessageId = result?.message?.id;
      if (insertedMessageId && mediaOptions?.mediaUrl) {
        try {
          await supabase.from('message_attachments').insert({
            message_id: insertedMessageId,
            file_name: mediaOptions.filename || 'arquivo',
            content_type: mediaOptions.mimetype || 'application/octet-stream',
            storage_path: mediaOptions.storagePath || '',
            original_url: mediaOptions.mediaUrl,
            file_size: mediaOptions.fileSize ?? 0,
          });
        } catch (attErr) {
          console.error('[useConversationMessages] Failed to save attachment metadata:', attErr);
          // Non-fatal: message was sent successfully.
        }
      }

      // Remover da lista de pendentes
      pendingMessageIds.current.delete(tempId);

      // Atualizar mensagem otimista com status enviado
      // Keep source as 'optimistic' - the Realtime handler will replace this message
      // with the real one from the database, avoiding duplication
      setMessages(prev =>
        prev.map(msg =>
          msg.id === tempId
            ? { ...msg, status: 'sent' }
            : msg
        )
      );


      // O realtime vai substituir a mensagem otimista pela real
      pendingMessageBody.current = null;
      isSendingRef.current = false;
      return { success: true };
    } catch (err) {
      console.error('[useConversationMessages] Send error:', err);
      
      // Remover da lista de pendentes
      pendingMessageIds.current.delete(tempId);

      // Marcar mensagem como erro
      setMessages(prev =>
        prev.map(msg =>
          msg.id === tempId
            ? { ...msg, status: 'failed' }
            : msg
        )
      );

      isSendingRef.current = false;
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Failed to send' 
      };
    }
  }, [contactId, leadId, phoneNumber, loadMessages]);

  // Setup Realtime subscription
  useEffect(() => {
    if (!contactId && !leadId && !phoneNumber && !sessionId) return;

    // Carregar mensagens iniciais
    loadMessages();

    // Criar filtro para o subscription
    let filter: string | undefined;
    if (sessionId && contactId) {
      filter = `contact_id=eq.${contactId}`;
    } else if (sessionId) {
      filter = `owner_session_id=eq.${sessionId}`;
    } else if (contactId) {
      filter = `contact_id=eq.${contactId}`;
    } else if (leadId) {
      filter = `lead_id=eq.${leadId}`;
    }
    // Para phone, não filtramos - processamos no handler

    const channelName = `conversation:${sessionId || contactId || leadId || phoneNumber}`;
    
    const channel = supabase
      .channel(channelName)
      // Escutar INSERT (novas mensagens)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: filter,
        },
        (payload: RealtimePostgresChangesPayload<Message>) => {
          const newMessage = payload.new as Message;
          
          // Filtrar por canal WhatsApp
          if (newMessage.channel !== 'whatsapp') return;

          // Unified support inbox: accept all CS-relevant sources
          const acceptedSources = new Set([
            'twilio_divus',
            'wppconnect_shared',
            'evolution_shared',
            'whatsapp_app',
            'system_notice',
            'internal_note',
          ]);

          if (!acceptedSources.has(newMessage.source || '')) return;

          // Atendimento panel guard: hide Commercial-thread messages
          // (owner_session_id IS NULL and not a system notice/internal note).
          if (sessionId && contactId
              && newMessage.owner_session_id === null
              && newMessage.source !== 'system_notice'
              && newMessage.source !== 'internal_note') {
            return;
          }

          if (sessionId && newMessage.direction === 'outbound' && !contactId) {
            if (newMessage.owner_session_id !== null && newMessage.owner_session_id !== sessionId) return;
          }

          if (sessionId && contactId && newMessage.direction === 'inbound' && newMessage.contact_id !== contactId) return;
          
          // Filtrar por lead_id se necessário
          if (leadId && newMessage.lead_id !== leadId) return;
          
          // Filtrar por phone se necessário (quando não temos contact/lead)
          if (phoneNumber && !contactId && !leadId && !sessionId) {
            const cleanPhone = phoneNumber.replace(/\D/g, '');
            const matchesFrom = newMessage.from_e164?.includes(cleanPhone);
            const matchesTo = newMessage.to_e164?.includes(cleanPhone);
            if (!matchesFrom && !matchesTo) return;
          }

          console.log('[Realtime] New message:', newMessage.id, newMessage.body?.substring(0, 30));

          setMessages(prev => {
            // 1. Verificar se já existe (evitar duplicata por ID)
            if (prev.some(m => m.id === newMessage.id)) return prev;

            // 2. Procurar mensagem otimista para substituir
            const optimisticIndex = prev.findIndex(m => {
              if (m.source !== 'optimistic' || m.direction !== 'outbound') return false;
              
              // Comparar body normalizado
              const normalizedOptimistic = normalizeBody(m.body);
              const normalizedNew = normalizeBody(newMessage.body);
              
              // Aceitar match exato OU match parcial (primeiro 20 chars)
              const bodyMatches = normalizedOptimistic === normalizedNew ||
                (normalizedOptimistic.length > 0 && normalizedNew.startsWith(normalizedOptimistic.substring(0, 20)));
              
              // Verificar janela de tempo (30 segundos)
              const timeDiff = Math.abs(
                new Date(m.created_at).getTime() - new Date(newMessage.created_at).getTime()
              );
              
              return bodyMatches && timeDiff < 30000;
            });

            if (optimisticIndex !== -1) {
              console.log('[Realtime] Replacing optimistic message at index', optimisticIndex);
              const updated = [...prev];
              updated[optimisticIndex] = newMessage;
              return updated;
            }

            // 3. Fallback: Se é outbound e temos mensagem otimista recente, substituir a última
            if (newMessage.direction === 'outbound') {
              // Encontrar último índice de mensagem otimista (compatível com ES5)
              let lastOptimisticIndex = -1;
              for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i].source === 'optimistic' && 
                    prev[i].direction === 'outbound' &&
                    prev[i].id.startsWith('temp_')) {
                  lastOptimisticIndex = i;
                  break;
                }
              }
              
              if (lastOptimisticIndex !== -1) {
                console.log('[Realtime] Fallback: Replacing last optimistic at index', lastOptimisticIndex);
                const updated = [...prev];
                updated[lastOptimisticIndex] = newMessage;
                return updated;
              }
            }

            // 4. É mensagem nova (inbound ou de outro remetente)
            return [...prev, newMessage];
          });
        }
      )
      // Escutar UPDATE (status changes: sent → delivered → read)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: filter,
        },
        (payload: RealtimePostgresChangesPayload<Message>) => {
          const updatedMessage = payload.new as Message;
          
          console.log('[Realtime] Message updated:', updatedMessage.id, updatedMessage.status);

          setMessages(prev =>
            prev.map(msg =>
              msg.id === updatedMessage.id ? updatedMessage : msg
            )
          );
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Subscription status:', status);
        setIsConnected(status === 'SUBSCRIBED');
      });

    // Cleanup
    return () => {
      console.log('[Realtime] Unsubscribing from:', channelName);
      supabase.removeChannel(channel);
    };
  }, [contactId, leadId, sessionId, phoneNumber, loadMessages]);

  return {
    messages,
    isLoading,
    error,
    isConnected,
    sendMessage,
    loadMore,
    hasMore,
    refetch: loadMessages,
  };
}
